import { useEffect, useState } from "react";
import { useGetFruitsQuery } from "../../Receiving/unloading/unloadingApi";
import { Col, Row } from "react-bootstrap";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import Button from "../../../components/shared/buttons/button";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import { useGetRefrigerationPalletsQuery } from "../../../app/globalApi";

import {
  useCreateBrokerageRefrigerationMutation,
  useGetBrokerageSummaryQuery,
  useUpdateBrokerageRefrigerationMutation,
  useLazyGetBrokeragePalletRecordsQuery,
} from "../materialEntered/materialApi";
import toast from "react-hot-toast";
import useTranslator from "../../../hooks/useTranslator";
import { REF_ALLOWED_10LB_BOXES, REF_ALLOWED_20LB_BOXES } from "../../../helpers/constants";
import { convertToUserTimezone, getCurrentDate, getCurrentDateTime } from "../../../helpers/Utils";

const RefrigerationModal = ({ show, setShow, record, mode, packingTypeOptions, onSuccess }) => {
  const { _t } = useTranslator();
  const [createData] = useCreateBrokerageRefrigerationMutation();
  const [updateData] = useUpdateBrokerageRefrigerationMutation();
  const currentDate = getCurrentDate("YYYY-MM-DD");    
  const currentDateTime = getCurrentDateTime();  
  const { data: summaryData } = useGetBrokerageSummaryQuery({
    tab: "refrigeration",
    date: currentDate,
  });

  // Available boxes by packing type from summary
  const maxCheeksPrime10lb = summaryData?.cheeks_prime_10lb_box;
  const maxCheeksPrime20lb = summaryData?.cheeks_prime_20lb_box;
  const maxStripsPrime20lb = summaryData?.strips_prime_20lb_box;
  const maxCheeksSeconds20lb = summaryData?.cheeks_seconds_20lb_box;
  const maxStripsSeconds20lb = summaryData?.strips_seconds_20lb_box;
  const maxPieces20lb = summaryData?.pieces_20lb_box;
  
  const { data: pallets } = useGetRefrigerationPalletsQuery();
  const [getPalletRecords, { data: palletRecords }] = useLazyGetBrokeragePalletRecordsQuery();
  

  const initialFormData = {
    status: "",
    pallet_code: "",
    fruit_type_id: "",
    packing_type: "",
    box_number: "",
    created_at: getCurrentDateTime(),
  };  
  const [formData, setFormData] = useState(initialFormData);
  const [localPallets, setLocalPallets] = useState([]);
  

  useEffect(() => {
    if (mode === "edit" && record) {
      setFormData({
        pallet_code: record.pallet_code || "",
        created_at: convertToUserTimezone(record.created_at),
        fruit_type_id: record.fruit_type_id || "",
        packing_type: record.packing_type || "",
        box_number: record.box_number || "",
        status: record.status || "",
      });
      
      // Fetch pallet records based on the existing packing_type in edit mode
      if (record.packing_type) {
        getPalletRecords({ packing_type: record.packing_type });
      }
    } else {
      setFormData(initialFormData);
    }
  }, [mode, record, show]);
  const handleClose = () => {
    setShow(false);
  };
  const { data: fruitList } = useGetFruitsQuery();
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate packing type is selected
    if (!formData.packing_type) {
      toast.error(_t("please_select_packing_type", "Please select a packing type"));
      return;
    }

    // Validate box number is entered
    if (!formData.box_number || parseInt(formData.box_number) <= 0) {
      toast.error(_t("please_enter_box_number", "Please enter box number"));
      return;
    }

    const totalBoxes = parseInt(formData.box_number) || 0;

    const is10Pounds = formData.packing_type === "prime_cheek_10_pounds";
    const maxBoxesPerPallet = is10Pounds ? REF_ALLOWED_10LB_BOXES : REF_ALLOWED_20LB_BOXES;

    // Validate box count against packing type limit (161 or 80)
    if (totalBoxes > maxBoxesPerPallet) {
      toast.error(
        _t(
          "boxes_exceed_pallet_limit",
          "Box count exceeds the limit of :limit boxes for this packing type",
          { limit: maxBoxesPerPallet }
        )
      );
      return;
    }


    const mutation = mode === "add" ? createData : updateData;

    try {
      const payload =
        mode === "add" ? formData : { id: record.id, requestBody: formData };
      await mutation(payload).unwrap();
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating refrigeration data:", error);
    }
  };

  
    // :::::::::: Pallet Config ::::::::::::
  
    useEffect(() => {
      // If packing_type is selected and we have pallet records from API, use those
      if (formData.packing_type && palletRecords?.data) {
        setLocalPallets([...palletRecords.data]);
      } 
      // Otherwise use all pallets
      else if (pallets?.data) {
        setLocalPallets([...pallets.data]);
      }
    }, [pallets, palletRecords, formData.packing_type]);
  
    // ::::::::::: END :::::::::::::

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    // When packing_type changes, fetch pallet records
    if (name === "packing_type" && value) {
      getPalletRecords({ packing_type: value });
    }
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <InputWithLabel
          label={_t("date_time", "Date & Time")}
          type="text"
          name="created_at"
          value={formData.created_at}
          disabled={true}
          onChange={handleChange}
        />        
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("fruit_type", "Fruit Type")}
          name="fruit_type_id"
          value={formData.fruit_type_id}
          options={fruitList?.data.map((list) => ({
            value: list.id,
            label: list.name,
          }))}
          onChange={handleChange}
        />
      </Col>
      
      <Col lg={6}>
        <SelectBox
          label={_t("packing_type", "Packing Type")}
          name="packing_type"
          value={formData.packing_type}
          options={packingTypeOptions}
          onChange={handleChange}
          required={true}
        />
      </Col>
      <Col lg={6}>
        <SelectBox 
          label={_t("select_pallet", "Select Pallet")}
          name="pallet_code"
          options={localPallets.map((list) => ({
            value: list.id,
            label: list.pallet_entered_cases > 0 
              ? `${list.pallet_name} / ${list.pallet_entered_cases} ${_t('boxes_label','Boxes')}`
              : list.pallet_name,
            disabled: list.pallet_status === "reserved",
          }))}
          value={formData.pallet_code}
          required={false}
          onChange={handleChange}
          className="w-100"
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("number_of_box", "Number of Box")}
          type="number"
          name="box_number"
          value={formData.box_number}
          onChange={handleChange}
          placeholder={"0"}
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          name="status"
          value={formData?.status}
          options={[
            { value: "stored", label: _t("stored","Stored") },
            { value: "sold", label: _t("sold","Sold") },
          ]}
          onChange={handleChange}
        />
      </Col>
    </>
  );
  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        mode === "add"
          ? _t(
              "adding_refrigeration_room_data",
              "Adding Refrigeration Room Data"
            )
          : _t(
              "editing_refrigeration_room_data",
              "Editing Refrigeration Room Data"
            )
      }
      children={formData}
    >
      <Row>{renderFormFields()}</Row>

      <Row>
        <Col className="text-end">
          <Button
            label={_t("save_changes", "Save Changes")}
            size="small"
            onClick={handleSubmit}
            // isLoading={isCreating || isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default RefrigerationModal;
